import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Chrome, Loader2 } from 'lucide-react';
import { shouldRedirectTeacherDashboardToPolicy } from '../utils/stagingWebApp';

const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/traffic-lite/fbgdjpgcehknbpaoigchadbjfibmpife?authuser=0&hl=en&pli=1';
const EXTENSION_CHECK_TIMEOUT_MS = 2000;

interface AuthStateMessage {
  source: string;
  action: string;
  signedIn: boolean;
  role?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
}

const LoginLandingPage = () => {
  const [status, setStatus] = useState<'checking' | 'install' | 'redirecting'>('checking');
  const appBaseUrl = window.location.origin + window.location.pathname;
  const getAppRouteUrl = (route: string) => `${appBaseUrl}#${route}`;
  const signInUrl = getAppRouteUrl('/signin');

  useEffect(() => {
    let resolved = false;

    const handleAuthState = (event: MessageEvent) => {
      const data = event.data as AuthStateMessage;
      if (!data || data.source !== 'trafficlite-extension' || data.action !== 'AUTH_STATE' || resolved) return;
      resolved = true;

      if (data.signedIn) {
        setStatus('redirecting');
        if (data.isSuperAdmin) {
          window.location.href = getAppRouteUrl('/admin/users');
        } else if (data.isAdmin) {
          window.location.href = getAppRouteUrl('/admin/settings');
        } else if (data.role === 'teacher') {
          window.location.href = getAppRouteUrl(
            shouldRedirectTeacherDashboardToPolicy() ? '/teacher/policy-settings' : '/dashboard'
          );
        } else {
          window.location.href = getAppRouteUrl(
            shouldRedirectTeacherDashboardToPolicy() ? '/teacher/policy-settings' : '/dashboard'
          );
        }
      } else {
        setStatus('redirecting');
        window.location.href = signInUrl;
      }
    };

    window.addEventListener('message', handleAuthState);

    // Request auth state from extension (content script will forward and respond)
    window.postMessage({ source: 'trafficlite-web', action: 'GET_AUTH_STATE' }, '*');

    const timer = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setStatus('install');
      }
    }, EXTENSION_CHECK_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleAuthState);
      clearTimeout(timer);
    };
  }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Checking your Traffic Lite status...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'redirecting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Redirecting...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // status === 'install'
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
            <img
              src="/icon.png"
              alt="Traffic Lite logo"
              className="h-16 w-16 object-contain"
            />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            Install Traffic Lite
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 mt-2">
            You need the Traffic Lite Chrome extension before you can sign in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <p className="text-gray-700">
              Click the button below to install Traffic Lite from the Chrome Web Store, then return here to sign in.
            </p>
            <Button
              asChild
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
            >
              <a
                href={CHROME_WEB_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center text-white"
              >
                <Chrome className="h-5 w-5 mr-2" />
                Install Traffic Lite
              </a>
            </Button>
            <p className="text-sm text-gray-500">
              Already installed?{' '}
              <a
                href={signInUrl}
                className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
              >
                Go to sign in
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginLandingPage;
